import { AxiosError } from "axios";
import { Option } from '../screens/ServiceAgreements/types';
import {
  getApi,
  noAuthGetApi,
  noAuthPostApi,
  noAuthPutApi,
  postApi,
  putApi,
} from "./apis";
const requestCache = new Map();

const cacheKey = (endpoint: string, params?: any) => {
  return `${endpoint}${params ? JSON.stringify(params) : ""}`;
};

const withCache = async (key: string, request: () => Promise<any>) => {
  if (requestCache.has(key)) {
    return requestCache.get(key);
  }

  const promise = request();
  requestCache.set(key, promise);

  try {
    const result = await promise;
    setTimeout(() => requestCache.delete(key), 2000);
    return result;
  } catch (error) {
    requestCache.delete(key);
    throw error;
  }
};
export const fetchSeedsDropdownData = async (type: string) => {
  let endpoint = "";

  switch (type) {
    case "calYear":
      endpoint = "ProductCostSeeds/GetCalendarYear";
      break;
    case "activity":
      endpoint = "ProductCostSeeds/GetActivity";
      break;
    case "cropType":
      endpoint = "ProductCostSeeds/GetCropType";
      break;
    default:
      throw new Error("Invalid dropdown type");
  }

  return withCache(cacheKey(endpoint), async () => {
    try {
      return await noAuthGetApi(endpoint);
    } catch (error: unknown) {
      if (error instanceof Error) {
        throw new Error(`Failed to fetch data for ${type}: ${error.message}`);
      }
      throw new Error(
        `Failed to fetch data for ${type}: unknown error occurred`
      );
    }
  });
};

export const fetchJdOpsProductData = async (type: any) => {
  let endpoint = `jdopsproduct/jdopsproducts`;
  try {
    return await getApi(endpoint, type);
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw new Error(`Failed to fetch data: ${error.message}`);
    }
    throw new Error("Failed to fetch data: unknown error occurred");
  }
};

export const getSeedsCostApi = async (param?: any) => {
  let endpoint = ``;
  if (param) {
    endpoint = `SeedCost/seedcosts?cropId=${param}`;
  } else {
    endpoint = `SeedCost/seedcosts`;
  }
  try {
    return await getApi(endpoint);
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw new Error(`Failed to fetch data: ${error.message}`);
    }
    throw new Error("Failed to get Seeds cost");
  }
};

export const getPlantingData = async () => {
  let endpoint = `PlantingActivity/plantingactivities`;
  try {
    return await getApi(endpoint);
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw new Error(`Failed to fetch data: ${error.message}`);
    }
    throw new Error("Failed to get Seeds cost");
  }
};

export const putPlantingCostApi = async (data: any) => {
  let endpoint = `PlantingActivity/plantingactivity`;
  try {
    return await putApi(endpoint, data);
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      throw new Error(`${error.response?.data.error || error.message}`);
    }
    throw new Error("Failed to fetch data: unknown error occurred");
  }
};

export const getHarvestData = async () => {
  let endpoint = `HarvestWork/harvestworks`;
  try {
    return await getApi(endpoint);
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw new Error(`Failed to fetch data: ${error.message}`);
    }
    throw new Error("Failed to get Seeds cost");
  }
};

export const putHarvestCostApi = async (data: any) => {
  let endpoint = `HarvestWork/harvestwork`;
  try {
    return await putApi(endpoint, data);
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      throw new Error(`${error.response?.data.error || error.message}`);
    }
    throw new Error("Failed to fetch data: unknown error occurred");
  }
};

export const getTillageData = async () => {
  let endpoint = `tillagework/tillagework`;
  try {
    return await getApi(endpoint);
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw new Error(`Failed to fetch data: ${error.message}`);
    }
    throw new Error("Failed to get Seeds cost");
  }
};

export const getRatesTypesApi = async () => {
  let endpoint = `tillagework/ratetypes`;
  try {
    return await getApi(endpoint);
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw new Error(`Failed to fetch data: ${error.message}`);
    }
    throw new Error("Failed to get Seeds cost");
  }
};

export const putTillageWorkApi = async (data: any) => {
  let endpoint = `tillagework/tillagework`;
  try {
    return await putApi(endpoint, data);
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      throw new Error(`${error.response?.data.error || error.message}`);
    }
    throw new Error("Failed to fetch data: unknown error occurred");
  }
};

export const getApplicationDataFromApi = async () => {
  let endpoint = `ApplicationActivity/applicationactivities`;
  try {
    return await getApi(endpoint);
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw new Error(`Failed to fetch data: ${error.message}`);
    }
    throw new Error(`Failed to fetch data: unknown error occurred`);
  }
};

export const putApplicationData = async (data: any) => {
  let endpoint = `ApplicationActivity/applicationactivity`;
  try {
    return await putApi(endpoint, data);
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      throw new Error(`${error.response?.data.error || error.message}`);
    }
    throw new Error("Failed to fetch data: unknown error occurred");
  }
};

export const putSeedsCostApi = async (data: any) => {
  let endpoint = `SeedCost/seedcosts`;
  try {
    return await putApi(endpoint, data);
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      const responseData = error.response?.data as
        | { error?: string; message?: string; title?: string }
        | undefined;

      const apiMessage =
        responseData?.error ||
        responseData?.message ||
        responseData?.title ||
        error.message;

      throw new Error(apiMessage || "Failed to save seeds cost.");
    }

    if (error instanceof Error) {
      throw new Error(error.message);
    }
    throw new Error("Failed to save seeds cost.");
  }
};

export const updateJDOpsProduct = async (type: any) => {
  let endpoint = `JdOpsProduct/jdopsproducts`;
  try {
    return await putApi(endpoint, type);
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw new Error(`Failed to fetch data: ${error.message}`);
    }
    throw new Error("Failed to fetch data: unknown error occurred");
  }
};

export const fetchProductNames = async (type: Option[]) => {
  let endpoint = `product/products`;
  try {
    return await getApi(endpoint, type);
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw new Error(`Failed to fetch data: ${error.message}`);
    }
    throw new Error(`Failed to fetch data: unknown error occurred`);
  }
};

export const GetproductPurchaseUnits = async () => {
  let endpoint = `Product/product-purchase-units`;
  try {
    return await getApi(endpoint);
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw new Error(`Failed to fetch data: ${error.message}`);
    }
    throw new Error(`Failed to fetch data: unknown error occurred`);
  }
};

export const updateProductPrices = async (data: any) => {
  let endpoint = `Product/products/pricing`;
  try {
    return await putApi(endpoint, data);
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw new Error(`Failed to fetch data: ${error.message}`);
    }
    throw new Error(`Failed to fetch data: unknown error occurred`);
  }
};

export const createProductPrices = async (data: any) => {
  let endpoint = `Product/products/pricing`;
  try {
    return await postApi(endpoint, data);
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw new Error(`Failed to fetch data: ${error.message}`);
    }
    throw new Error(`Failed to fetch data: unknown error occurred`);
  }
};

export const getProductsDetails = async (type: any[]) => {
  let endpoint = `Product/products/details`;
  try {
    return await getApi(endpoint, type);
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw new Error(`Failed to fetch data: ${error.message}`);
    }
    throw new Error(`Failed to fetch data: unknown error occurred`);
  }
};

export const updateProductWithPrice = async (type: any) => {
  let endpoint = `Product/products/pricing`;
  try {
    return await putApi(endpoint, type);
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw new Error(`Failed to fetch data: ${error.message}`);
    }
    throw new Error(`Failed to fetch data: unknown error occurred`);
  }
};

export const fetchSeedsVaritiesName = async (
  value: string,
  calyear: number
) => {
  const endpoint = `ProductCostSeeds/GetVarities?CropType=${value}&CalYear=${calyear}`;
  return withCache(cacheKey(endpoint), async () => {
    try {
      return await noAuthGetApi(endpoint);
    } catch (error: unknown) {
      if (error instanceof Error) {
        throw new Error(`Failed to fetch data: ${error.message}`);
      }
      throw new Error("Failed to fetch data: unknown error occurred");
    }
  });
};

export const fetchSeedsDropdown = async (
  type: string,
  value: number
): Promise<any> => {
  let endpoint = "";

  switch (type) {
    case "genericName":
      endpoint = `ProductCostSeeds/GetGenericName?Id=${value}`;
      break;
    case "price":
      endpoint = `ProductCostSeeds/GetPrice?Id=${value}`;
      break;
    case "quantityUnit":
      endpoint = `ProductCostSeeds/GetQuantityUnits?Id=${value}`;
      break;
    case "seedsCount":
      endpoint = `ProductCostSeeds/GetSeedsCount?Id=${value}`;
      break;
    case "perSeedCost":
      endpoint = `ProductCostSeeds/GetPerSeedCost?Id=${value}`;
      break;
    case "bagsPerBushel":
      endpoint = `ProductCostSeeds/GetBagsPerBushel?Id=${value}`;
      break;
    case "perBushelCost":
      endpoint = `ProductCostSeeds/GetPerBushelCost?Id=${value}`;
      break;
    default:
      throw new Error("Invalid dropdown type");
  }
  return withCache(cacheKey(endpoint), async () => {
    try {
      return await noAuthGetApi(endpoint);
    } catch (error: unknown) {
      if (error instanceof Error) {
        throw new Error(`Failed to fetch data: ${error.message}`);
      }
      throw new Error("Failed to fetch data: unknown error occurred");
    }
  });
};

export const postSeedsData = async (data: any) => {
  try {
    return await noAuthPostApi(
      "ProductCostSeeds/AddProductCostSeedsGridView",
      data
    );
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw new Error(`Failed to fetch data : ${error.message}`);
    } else {
      throw new Error("Failed to fetch data : unknown error occured");
    }
  }
};

export const fetchSeedsGrid = async (value: number) => {
  const endpoint = `ProductCostSeeds/GetAllProductCostSeedsGridViewData?userId=${value}`;
  return withCache(cacheKey(endpoint), async () => {
    try {
      return await noAuthGetApi(endpoint);
    } catch (error: unknown) {
      if (error instanceof Error) {
        throw new Error(`Failed to fetch data: ${error.message}`);
      }
      throw new Error("Failed to fetch data: unknown error occurred");
    }
  });
};

// export const fetchFertilizersGrid = async(value:any) => {
//   const endpoint = `Fertilizer`;
//    try {
//       return await postApi(endpoint, value);
//     } catch (error: unknown) {
//       if (error instanceof Error) {
//         throw new Error(`Failed to fetch data: ${error.message}`);
//       }
//       throw new Error('Failed to fetch data: unknown error occurred');
//     }

// }

// export const getHerbicidesGrid = async(value: any) => {
//   const endpoint = `Herbicide`;
//   try {
//       return await postApi(endpoint,value);
//     } catch (error: unknown) {
//       if (error instanceof Error) {
//         throw new Error(`Failed to fetch data: ${error.message}`);
//       }
//       throw new Error('Failed to fetch data: unknown error occurred');
//     }
// }

export const updatedSeedsGrid = async (data: any) => {
  try {
    return await noAuthPostApi(
      "ProductCostSeeds/ApproveProductCostSeedsData",
      data
    );
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw new Error(`Failed to fetch data : ${error.message}`);
    } else {
      throw new Error("Failed to fetch data : unknown error occured");
    }
  }
};

export const updateSeedsGridView = async (data: any) => {
  try {
    return await noAuthPutApi("ProductCostSeeds/EditGridViewData", data);
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw new Error(`Failed to fetch data : ${error.message}`);
    } else {
      throw new Error("Failed to fetch data : unknown error occured");
    }
  }
};

export const deleteSeedsGridView = async (data: any) => {
  try {
    return await noAuthPutApi(`ProductCostSeeds/DeleteGridViewData?Id=${data}`);
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw new Error(`Failed to fetch data : ${error.message}`);
    } else {
      throw new Error("Failed to fetch data : unknown error occured");
    }
  }
};
